import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Radio, Plus, Play, Pencil, Trash2, Presentation, LogOut, Users, Loader2, Palette,
} from "lucide-react";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/presentations");
      setItems(data);
    } catch {
      setItems([]);
    }
  };
  useEffect(() => { load(); }, []);

  const createNew = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/presentations", {
        title: "Untitled presentation",
        slides: [],
      });
      navigate(`/edit/${data.id}`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const present = async (p) => {
    if (!p.slides?.length) {
      toast.error("Add at least one slide first");
      return;
    }
    try {
      const { data } = await api.post(`/presentations/${p.id}/sessions`);
      navigate(`/present/${data.code}`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try {
      await api.delete(`/presentations/${p.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-20 bg-white/90 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 font-display font-extrabold text-lg text-slate-900">
            <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white">
              <Radio className="w-4 h-4" />
            </span>
            <span>Pulse</span>
          </div>
          <div className="flex items-center gap-3">
            {user?.logo_url && (
              <img src={`${BACKEND_URL}${user.logo_url}`} alt="logo" className="h-12 w-auto max-w-[220px] object-contain" />
            )}
            <button data-testid="branding-link" onClick={() => navigate("/branding")}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-indigo-600 transition-colors">
              <Palette className="w-4 h-4" /> Branding
            </button>
            {user?.role === "admin" && (
              <button data-testid="admin-users-link" onClick={() => navigate("/admin")}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-indigo-600 transition-colors">
                <Users className="w-4 h-4" /> Users
              </button>
            )}
            <span className="text-sm text-slate-500 hidden sm:block">{user?.name}</span>
            <button data-testid="logout-button" onClick={() => { logout(); navigate("/login"); }}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-red-600 transition-colors">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10" data-testid="presenter-dashboard">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">Your presentations</h1>
            <p className="text-slate-500 mt-1">Build interactive slides and present them live.</p>
          </div>
          <button data-testid="create-presentation-button" onClick={createNew} disabled={busy}
                  className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98] text-white font-semibold rounded-xl px-5 py-2.5 shadow-md shadow-indigo-200 transition-all disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} New
          </button>
        </div>

        {items === null ? (
          <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-indigo-600 animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-24 border-2 border-dashed border-slate-200 rounded-3xl">
            <Presentation className="w-12 h-12 text-slate-300 mx-auto" />
            <p className="mt-4 text-slate-500">No presentations yet.</p>
            <button onClick={createNew} className="mt-4 text-indigo-600 font-semibold hover:underline">Create your first one</button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((p) => (
              <div key={p.id} data-testid={`presentation-card-${p.id}`}
                   className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition-all flex flex-col justify-between group">
                <div onClick={() => navigate(`/edit/${p.id}`)} className="cursor-pointer">
                  <div className="aspect-video rounded-xl bg-gradient-to-br from-indigo-50 to-violet-50 border border-slate-100 flex items-center justify-center mb-4">
                    <Presentation className="w-8 h-8 text-indigo-400" />
                  </div>
                  <h3 className="font-display font-bold text-slate-900 text-lg truncate">{p.title}</h3>
                  <p className="text-sm text-slate-400 mt-0.5">{p.slides?.length || 0} slides</p>
                </div>
                <div className="flex items-center gap-2 mt-5">
                  <button data-testid={`present-button-${p.id}`} onClick={() => present(p)}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg px-3 py-2 transition-all">
                    <Play className="w-4 h-4" /> Present
                  </button>
                  <button onClick={() => navigate(`/edit/${p.id}`)}
                          className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button data-testid={`delete-button-${p.id}`} onClick={() => remove(p)}
                          className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-red-50 hover:text-red-600 transition-all">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
